import React, {useCallback, useMemo} from "react";
import SortableTable, {TableColumn, TableData} from "@common/SortableTable";
import DisplayHelpers from "@/helpers/display_helpers";
import {SurveyModel} from "survey-core";
import AvatarDetails from "@common/AvatarDetails";
import {User} from "@/api/dto/dto";
import {aiAgentUser} from "@/components/pages/tasks/AssignedToTask";
import {SortOrderType} from "@clients/ClientsPage";
import Helpers from "@/helpers/helpers";
import {useAuthStore} from "@/store/auth_store";
import {CareJourneyType, TaskStepName} from "@/api/dto/care-journey";
import {ViewAttempt} from "@/components/pages/tasks/task_edit/progress/TaskStepHistoryTable.styles";
import {convertTimezoneToLocal, getTimezoneAbbreviation} from "@/helpers/timezone_helpers";

type Props = {
  attempts: Record<string, any>[];
  stepName: string;
  surveyModel: SurveyModel
  loading: boolean;
  error: Error | null;
  onViewAttempt(data: any): void;
  journeyType?: string
}

const surveyMappedColumns = ['outreach_outcome'];

const sortableColumnsByStep = {
  [TaskStepName.INITIAL_OUTREACH]: ['attempt', 'date_of_attempt', 'owner', 'outreach_outcome'],
  [TaskStepName.SCHEDULED_APPOINTMENT]: ['pcp_appointment', 'date_of_appointment', 'scheduled_on', 'owner']
};

const stepHeaders = (stepName: string) => {
  switch (stepName) {
  case TaskStepName.INITIAL_OUTREACH:
    return ['Attempt', 'Date & time of attempt', 'Outreach owner', 'Outcome'];
  case TaskStepName.SCHEDULED_APPOINTMENT:
    return ['PCP appointment', 'Date & time of appointment', 'Scheduled on', 'By'];
  default:
    return [];
  }
};

const getAppointmentScheduledFieldName = (journeyType?: string) => {
  switch (journeyType) {
  case CareJourneyType.BH_SCHEDULE_APPOINTMENT:
    return 'appointment_scheduled';
  default:
    return 'pcp_appointment';
  }
};

const getAppointmentDateFieldName = (journeyType?: string) => {
  switch (journeyType) {
  case CareJourneyType.BH_SCHEDULE_APPOINTMENT:
    return 'appointment_datetime';
  default:
    return 'pcp_appointment_date';
  }
};

const TaskStepHistoryTable = ({attempts, stepName, surveyModel, loading, error, onViewAttempt, journeyType} : Props) => {
  const {currentUser} = useAuthStore();

  const tableData = useMemo<TableData[]>(() => {
    const mappedQuestions = surveyModel.getAllQuestions()
      .filter(question => surveyMappedColumns.includes(question.name));

    const mapAttemptToTableData = (attempt: Record<string, any>, index: number): TableData | null => {
      const data = attempt.data as any;
      const attemptOwner = attempt.submittedBy as User;

      if (!data) return null;

      const collector = attempt.collector_type?.toLowerCase();
      const ownerCell = {
        tableHeader: collector === 'human' ? 'Outreach owner' : 'By',
        key: 'owner',
        type: 'string' as const,
        value: collector === 'human' ? attemptOwner?.id : 'AI',
        displayValue: collector === 'human'
          ? attemptOwner ? <AvatarDetails user={attemptOwner} /> : ''
          : <AvatarDetails user={aiAgentUser} />,
      };

      if (stepName === TaskStepName.INITIAL_OUTREACH) {
        const outcomeValue = mappedQuestions?.find(q => q.name === 'outreach_outcome')?.choices
          ?.find((c: Record<string, unknown>) => c.value === data.outreach_outcome)?.text;

        return {
          attempt: {
            tableHeader: 'Attempt', key: 'attempt', type: 'number', value: index + 1,
            displayValue: <ViewAttempt onClick={() => onViewAttempt(data)}>{index + 1} (View)</ViewAttempt>,
            columnWidth: '20%'
          },
          date_of_attempt: {
            tableHeader: 'Date & time of attempt', key: 'date_of_attempt', type: 'date',
            value: data.date_of_attempt ?? '',
            displayValue: DisplayHelpers.formatDateTimeShort(data.date_of_attempt ?? ''),
            columnWidth: '30%'
          },
          owner: { ...ownerCell, tableHeader: 'Outreach owner', columnWidth: '20%' },
          outreach_outcome: {
            tableHeader: 'Outcome', key: 'outreach_outcome', type: 'string',
            value: outcomeValue, displayValue: outcomeValue, columnWidth: '30%'
          }
        };
      }

      if (stepName === TaskStepName.SCHEDULED_APPOINTMENT) {
        const appointmentScheduledName = getAppointmentScheduledFieldName(journeyType);
        const appointmentDateName = getAppointmentDateFieldName(journeyType);
        const appointmentTz = data.appointment_timezone;
        const dateValue = data[appointmentDateName] || data.date_of_attempt;
        let displayDate = appointmentTz && data[appointmentDateName]
          ? DisplayHelpers.formatDateTimeShort(convertTimezoneToLocal(data[appointmentDateName], appointmentTz))
          : DisplayHelpers.formatDateTimeShort(dateValue ?? '');
        if (appointmentTz) {
          const abbr = getTimezoneAbbreviation(appointmentTz);
          if (abbr) displayDate += ` (${abbr})`;
        }

        return {
          pcp_appointment: {
            tableHeader: 'PCP appointment', key: 'pcp_appointment', type: 'string',
            value: data[appointmentScheduledName] ? 'Scheduled' : 'Not scheduled',
            displayValue: data[appointmentScheduledName] ? 'Scheduled' : 'Not scheduled',
            columnWidth: '25%'
          },
          date_of_appointment: {
            tableHeader: 'Date & time of appointment', key: 'date_of_appointment', type: 'date',
            value: dateValue ?? '', displayValue: displayDate, columnWidth: '25%'
          },
          scheduled_on: {
            tableHeader: 'Scheduled on', key: 'scheduled_on', type: 'date',
            value: new Date(attempt.attempted_at).toISOString(),
            displayValue: DisplayHelpers.formatDate(attempt.attempted_at),
            columnWidth: '25%'
          },
          owner: { ...ownerCell, tableHeader: 'By', columnWidth: '25%' }
        };
      }

      return null;
    };

    return attempts
      .map((attempt, index) => mapAttemptToTableData(attempt, index))
      .filter((item): item is TableData => item !== null);
  }, [attempts, surveyModel, stepName, onViewAttempt]);

  const tableHeaders = stepHeaders(stepName);
  const sortFunction = useCallback((sortKey: TableColumn, sortOrder: SortOrderType, sortingTableData?: TableData[]) => {
    if(!sortingTableData) return;

    if(sortKey.key === 'owner') {
      return Helpers.sortByCurrentUser(sortingTableData, sortKey.key, sortOrder.value, currentUser);
    }

    return Helpers.sort(sortingTableData, sortKey.type, sortKey.key, sortOrder.value);
  }, [currentUser]);

  if(tableHeaders.length === 0) {
    return <></>;
  }

  const sortableColumns = tableData.length > 0 ? sortableColumnsByStep[stepName as keyof typeof sortableColumnsByStep]?.
    map(column => tableData[0][column]).filter(column => column) ?? [] : [];

  return (
    <SortableTable
      id={stepName}
      loading={loading}
      loadError={error}
      searchColumns={[]}
      sortableColumns={sortableColumns}
      searchPlaceholder={""}
      data={tableData}
      header={tableHeaders}
      excludeFilterAndSearch={true}
      tableOverrides={{
        sortFunction
      }}
    />
  );
};

export default TaskStepHistoryTable;

